import React from 'react';
import { Trophy, X, Award, CheckCircle2 } from 'lucide-react';
import { BADGE_ICONS, BADGE_COLORS } from './constants';

// Achievements Badge Component
export const AchievementsBadge = ({ achievements, showModal, setShowModal }) => {
  const earnedCount = achievements?.badges_earned || 0;
  const totalCount = achievements?.badges_total || 0;
  const points = achievements?.total_points || 0;
  
  return (
    <button
      onClick={() => setShowModal(true)}
      className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
      data-testid="achievements-badge-btn"
    >
      <Award className="text-yellow-300" size={18} />
      <span className="text-sm font-medium">{earnedCount}/{totalCount} Badges</span>
      <span className="text-xs bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded font-semibold">{points} pts</span>
    </button>
  );
};

// Achievements Modal Component
export const AchievementsModal = ({ isOpen, onClose, achievements }) => {
  if (!isOpen) return null;
  
  const badges = achievements?.badges || [];
  const earnedBadges = badges.filter(b => b.earned);
  const lockedBadges = badges.filter(b => !b.earned);
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="text-white" size={28} />
              <div>
                <h3 className="text-xl font-bold text-white">Your Achievements</h3>
                <p className="text-amber-100 text-sm">Track your progress and earn badges</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X size={24} />
            </button>
          </div>
          
          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{achievements?.total_points || 0}</p>
              <p className="text-xs text-amber-100">Total Points</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{achievements?.badges_earned || 0}</p>
              <p className="text-xs text-amber-100">Badges Earned</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{achievements?.stats?.analyses_count || 0}</p>
              <p className="text-xs text-amber-100">Analyses</p>
            </div>
          </div>
        </div>
        
        {/* Badges List */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Earned Badges */}
          {earnedBadges.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Earned ({earnedBadges.length})
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {earnedBadges.map(badge => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Award;
                  const colors = BADGE_COLORS[badge.color] || BADGE_COLORS.blue;
                  
                  return (
                    <div 
                      key={badge.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 border-transparent ring-2 ${colors.ring} bg-gradient-to-r from-white to-gray-50`}
                    >
                      <div className={`p-2.5 rounded-lg ${colors.bg}`}>
                        <IconComponent className={colors.text} size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{badge.name}</p>
                        <p className="text-xs text-gray-500 truncate">{badge.description}</p>
                        <p className="text-xs text-emerald-600 font-medium mt-0.5">+{badge.points} pts</p>
                      </div>
                      <CheckCircle2 className="text-emerald-500 flex-shrink-0" size={20} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Locked Badges */}
          {lockedBadges.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                In Progress ({lockedBadges.length})
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {lockedBadges.map(badge => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Award;
                  const progressPercent = badge.progress_percent || 0;
                  
                  return (
                    <div 
                      key={badge.id}
                      className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50/50"
                    >
                      <div className="p-2.5 rounded-lg bg-gray-200">
                        <IconComponent className="text-gray-400" size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-600 truncate">{badge.name}</p>
                        <p className="text-xs text-gray-400 truncate">{badge.description}</p>
                        <div className="mt-1.5">
                          <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                            <span>{badge.progress}/{badge.criteria?.threshold}</span>
                            <span>{progressPercent}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all"
                              style={{ width: `${progressPercent}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
